/**
 * THDSCLayout.jsx
 *
 * TH-variant composition of the Drill String Configurator. Renders:
 *   - DescriptionCard (quieter Sandvik info banner)
 *   - CouplingToggle + BitAdapterToggle (top-strip toggles)
 *   - Slot card row (3 / 4 / 5 cards depending on toggle state)
 *   - DSCResultsTabs (canonical tab order)
 *   - DSCResultsTabPanel (filter sidebar + PLP grid)
 *   - RestartConfiguratorButton (top-right of results section)
 *
 * Mobile responsive: vertical stack < 1024px (per plan §SP2.5 + brainstorm
 * wishlist #6 + DTH #5 generalised to TH).
 *
 * Citations (port-first):
 *  - Legacy LWC `searchResults.html` lines 198-312 — the DSC page shell.
 *  - Legacy LWC `addToDSC.html` lines 14-25 — the `slds-grid` slot-row
 *    wrapper around the per-slot `dscProductCard` instances. PWA mirrors
 *    this with a flex / grid container, no SLDS dependency.
 *  - Group D primitives reused VERBATIM (no invention):
 *    - SearchableDropdown   (`src/components/ui/SearchableDropdown.jsx`)
 *      via DSCDropdownRow.
 *    - FacetSection         (`src/components/product/FacetSection.jsx`)
 *      via DSCResultsTabPanel.
 *    - PLPProductCard       (`src/components/product/PLPProductCard.jsx`)
 *      via DSCResultsTabPanel.
 *    - RegionAvailabilityBadge (`src/components/wizard/RegionAvailabilityBadge.jsx`)
 *      via DSCResultsTabPanel.
 *    - useFilters           (`src/hooks/useFilters.js`)
 *      via DSCResultsTabPanel.
 *  - SP1 foundations (driven via DSCContext + cascade reducer):
 *    - DSCContext hooks: useDSC, useDSCSlot, useDSCToggle.
 *    - dscSlotConfig.js: DSC_SLOT_CONFIG, getSlotOrder, getTabOrder,
 *      DSC_TAB_FACETS.
 *  - Plan §SP2.1-2.7: layout, dropdown wiring, mobile-responsive vertical
 *    layout, restart button placement, description card.
 *  - Brainstorm wishlist #10, #11, #12, #13: visual states, tab grey-out,
 *    description quieter, restart rename.
 *
 * @module components/dsc/THDSCLayout
 * @since 2026-05-20 (Group E SP2 — TH composition)
 */

import { useState, useMemo, useCallback } from 'react';
import { useDSC, useDSCSlot } from '@contexts/DSCContext';
import { useCart } from '@contexts/CartContext';
import useMediaQuery from '@hooks/useMediaQuery';
import useAuth from '@hooks/useAuth';
import { DSC_ACTIONS } from '@services/dscCascade';
import { filterProductsForTab } from '@services/dscTabFilter';
import { DSC_SLOT_CONFIG, DSC_TAB_FACETS } from '@data/dscSlotConfig';
import DSCSlotCard from './DSCSlotCard';
import DSCDropdownRow from './DSCDropdownRow';
import CouplingToggle from './CouplingToggle';
import BitAdapterToggle from './BitAdapterToggle';
import DescriptionCard from './DescriptionCard';
import DSCResultsTabs from './DSCResultsTabs';
import DSCResultsTabPanel from './DSCResultsTabPanel';
import RestartConfiguratorButton from './RestartConfiguratorButton';
import MiniCart from '@components/layout/MiniCart';

// ============================================================================
// Per-slot dropdown wiring (TH)
// ============================================================================

/**
 * Lookup of the primary-dropdown key per slot. Mirrors plan §SP2.2 dropdown-
 * wiring table:
 *   shank-adapter: "Which rock drill are you using?" → manufacture → model.
 *   couplings:     "Which type of coupling sleeve?" → couplingType + threadForRod.
 *   rod:           "Which thread does your bit support?" → thread.
 *   bit-adapter:   "Which adapter thread?" → thread.
 *   drill-bit:     "Which type of bit?" → type.
 */
function THShankSlot() {
  const { dispatch, state } = useDSC();
  const cfg = DSC_SLOT_CONFIG.TH['shank-adapter'];
  const slot = useDSCSlot('shank-adapter');

  // Mfr + model dropdowns source from rockDrillManufacturers.json — SAME
  // canonical data the Replace path (StepEquipmentSelect) uses. Earlier
  // product-row-derived approach showed wrong models for Epiroc (CH32 etc.
  // — those aren't Epiroc); this catalog carries the correct COP-series.
  // Vandana 20 May 2026 walkthrough.
  const mfrData = state.rockDrillManufacturers?.manufacturers || [];

  const mfrOptions = useMemo(
    () =>
      mfrData
        .map((m) => ({ label: m.name, value: m.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [mfrData]
  );

  const modelOptions = useMemo(() => {
    if (!slot.value?.mfr) return [];
    const mfg = mfrData.find((m) => m.name === slot.value.mfr);
    if (!mfg || !Array.isArray(mfg.models)) return [];
    return mfg.models
      .map((m) => ({ label: m, value: m }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [mfrData, slot.value?.mfr]);

  const selectedMfr = useMemo(
    () => mfrOptions.find((o) => o.value === slot.value?.mfr) || null,
    [mfrOptions, slot.value?.mfr]
  );
  const selectedModel = useMemo(
    () => modelOptions.find((o) => o.value === slot.value?.model) || null,
    [modelOptions, slot.value?.model]
  );

  const isFilled = !!(slot.value?.mfr && slot.value?.model);
  const filledLabel = cfg.filledChipFormat(slot.value);

  return (
    <DSCSlotCard
      title={cfg.title}
      image={cfg.image}
      isFilled={isFilled}
      filledLabel={filledLabel}
      onClear={slot.clear}
      dataTestid="dsc-slot-card-shank-adapter"
    >
      <DSCDropdownRow
        question={cfg.dropdownLabel}
        items={mfrOptions}
        value={selectedMfr}
        onChange={(item) =>
          dispatch({
            type: DSC_ACTIONS.SET_SELECTION,
            slotId: 'shank-adapter',
            dropdownKey: 'manufacture',
            value: item?.value ?? 'None',
          })
        }
        placeholder="Select manufacturer..."
        dataTestid="dropdown-shank-adapter-mfr"
      />
      {slot.value?.mfr && (
        <DSCDropdownRow
          question="Which rock-drill model?"
          items={modelOptions}
          value={selectedModel}
          onChange={(item) =>
            dispatch({
              type: DSC_ACTIONS.SET_SELECTION,
              slotId: 'shank-adapter',
              dropdownKey: 'model',
              value: item?.value ?? 'None',
            })
          }
          placeholder="Select model..."
          dataTestid="dropdown-shank-adapter-model"
        />
      )}
    </DSCSlotCard>
  );
}

function THCouplingsSlot() {
  const { dispatch, state } = useDSC();
  const cfg = DSC_SLOT_CONFIG.TH.couplings;
  const slot = useDSCSlot('couplings');

  // Plan §SP2.2 couplings: "Reduction sleeve / Coupling sleeve" radio +
  // thread-for-rod dropdown. We use SearchableDropdown for both (mfr keeps
  // the typeahead UX consistent across the row).
  const couplingTypeOptions = useMemo(
    () => [
      { label: 'Reduction sleeve', value: 'Reduction Coupling' },
      { label: 'Coupling sleeve', value: 'Normal' },
    ],
    []
  );
  const threadForRodOptions = state.dropdownOptions?.['couplings:threadForRod'] || [];

  const selectedType = useMemo(
    () => couplingTypeOptions.find((o) => o.value === slot.value?.type) || null,
    [couplingTypeOptions, slot.value?.type]
  );
  const selectedThread = useMemo(
    () => threadForRodOptions.find((o) => o.value === slot.value?.rodThread) || null,
    [threadForRodOptions, slot.value?.rodThread]
  );

  const isFilled = !!slot.value?.type;
  const filledLabel = cfg.filledChipFormat(slot.value);
  const upstreamReady = !!state.selections['shank-adapter']?.model;

  return (
    <DSCSlotCard
      title={cfg.title}
      image={cfg.image}
      isFilled={isFilled}
      filledLabel={filledLabel}
      isDisabled={!upstreamReady}
      onClear={slot.clear}
      dataTestid="dsc-slot-card-couplings"
    >
      <DSCDropdownRow
        question={cfg.dropdownLabel}
        items={couplingTypeOptions}
        value={selectedType}
        disabled={!upstreamReady}
        onChange={(item) =>
          dispatch({
            type: DSC_ACTIONS.SET_SELECTION,
            slotId: 'couplings',
            dropdownKey: 'couplingType',
            value: item?.value ?? 'None',
          })
        }
        placeholder="Select coupling type..."
        dataTestid="dropdown-couplings-type"
      />
      {slot.value?.type && (
        <DSCDropdownRow
          question="Which thread for the rod?"
          items={threadForRodOptions}
          value={selectedThread}
          onChange={(item) =>
            dispatch({
              type: DSC_ACTIONS.SET_SELECTION,
              slotId: 'couplings',
              dropdownKey: 'threadForRod',
              value: item?.value ?? 'None',
            })
          }
          placeholder="Select thread..."
          dataTestid="dropdown-couplings-thread"
        />
      )}
    </DSCSlotCard>
  );
}

function THRodSlot({ couplingOn }) {
  const { dispatch, state } = useDSC();
  const cfg = DSC_SLOT_CONFIG.TH.rod;
  const slot = useDSCSlot('rod');
  const threadOptions = state.dropdownOptions?.['rod:thread'] || [];

  const selected = useMemo(
    () => threadOptions.find((o) => o.value === slot.value?.thread) || null,
    [threadOptions, slot.value?.thread]
  );

  const isFilled = !!slot.value?.thread;
  const filledLabel = cfg.filledChipFormat(slot.value);
  // Rod is enabled when shank-adapter is picked (no coupling required) OR when
  // couplings.type is picked (coupling-on path).
  const upstreamReady = couplingOn
    ? !!state.selections.couplings?.type
    : !!state.selections['shank-adapter']?.model;

  return (
    <DSCSlotCard
      title={cfg.title}
      image={cfg.image}
      imageOverride={couplingOn ? cfg.imageWithCoupling : undefined}
      isFilled={isFilled}
      filledLabel={filledLabel}
      isDisabled={!upstreamReady}
      onClear={slot.clear}
      dataTestid="dsc-slot-card-rod"
    >
      <DSCDropdownRow
        question={cfg.dropdownLabel}
        items={threadOptions}
        value={selected}
        disabled={!upstreamReady}
        onChange={(item) =>
          dispatch({
            type: DSC_ACTIONS.SET_SELECTION,
            slotId: 'rod',
            dropdownKey: 'thread',
            value: item?.value ?? 'None',
          })
        }
        placeholder="Select thread..."
        dataTestid="dropdown-rod-thread"
      />
    </DSCSlotCard>
  );
}

function THBitAdapterSlot() {
  const { dispatch, state } = useDSC();
  const cfg = DSC_SLOT_CONFIG.TH['bit-adapter'];
  const slot = useDSCSlot('bit-adapter');
  // No legacy cascade — for now we surface a flat thread list when one is
  // computed by the cascade reducer; SP2 ships with the wiring placeholder
  // (brainstorm §A: "Bit adapter wiring TBD — Vandana to confirm exact
  // cascade source"). Default to empty array.
  const threadOptions = state.dropdownOptions?.['bit-adapter:thread'] || [];

  const selected = useMemo(
    () => threadOptions.find((o) => o.value === slot.value?.thread) || null,
    [threadOptions, slot.value?.thread]
  );

  const isFilled = !!slot.value?.thread;
  const filledLabel = cfg.filledChipFormat(slot.value);
  const upstreamReady = !!state.selections.rod?.thread;

  return (
    <DSCSlotCard
      title={cfg.title}
      image={cfg.image}
      isFilled={isFilled}
      filledLabel={filledLabel}
      isDisabled={!upstreamReady}
      onClear={slot.clear}
      dataTestid="dsc-slot-card-bit-adapter"
    >
      <DSCDropdownRow
        question={cfg.dropdownLabel}
        items={threadOptions}
        value={selected}
        disabled={!upstreamReady}
        onChange={(item) =>
          dispatch({
            type: DSC_ACTIONS.SET_SELECTION,
            slotId: 'bit-adapter',
            dropdownKey: 'thread',
            value: item?.value ?? 'None',
          })
        }
        placeholder="Select adapter thread..."
        dataTestid="dropdown-bit-adapter-thread"
      />
    </DSCSlotCard>
  );
}

function THDrillBitSlot() {
  const { dispatch, state } = useDSC();
  const cfg = DSC_SLOT_CONFIG.TH['drill-bit'];
  const slot = useDSCSlot('drill-bit');

  // Bit-type options come from reduceRodSlot (dscCascade.js line ~307):
  // distinct buttonShape values from filteringData filtered on the picked
  // (mfr, model, thread). Legacy parity (searchResults.js:1414-1428) — NOT
  // a hardcoded 4-value list. The cascade prepends 'Select' via
  // uniqueLabelValueOptions, so we use the array as-is. Falls back to
  // a single 'Select' entry until rod thread is picked.
  const bitTypeOptions = useMemo(() => {
    const cascadeOpts = state.dropdownOptions?.['drill-bit:buttonShape'];
    if (Array.isArray(cascadeOpts) && cascadeOpts.length > 0) return cascadeOpts;
    return [{ label: 'Select', value: 'None' }];
  }, [state.dropdownOptions]);

  const selected = useMemo(
    () => bitTypeOptions.find((o) => o.value === slot.value?.type) || null,
    [bitTypeOptions, slot.value?.type]
  );

  const isFilled = !!slot.value?.type;
  const filledLabel = cfg.filledChipFormat(slot.value);
  const upstreamReady = !!state.selections.rod?.thread;

  return (
    <DSCSlotCard
      title={cfg.title}
      image={cfg.image}
      isFilled={isFilled}
      filledLabel={filledLabel}
      isDisabled={!upstreamReady}
      onClear={slot.clear}
      dataTestid="dsc-slot-card-drill-bit"
    >
      <DSCDropdownRow
        question={cfg.dropdownLabel}
        items={bitTypeOptions}
        value={selected}
        disabled={!upstreamReady}
        onChange={(item) =>
          dispatch({
            type: DSC_ACTIONS.SET_SELECTION,
            slotId: 'drill-bit',
            dropdownKey: 'type',
            value: item?.value ?? 'None',
          })
        }
        placeholder="Select bit type..."
        dataTestid="dropdown-drill-bit-type"
      />
    </DSCSlotCard>
  );
}

// ============================================================================
// THDSCLayout — top-level composition
// ============================================================================

export default function THDSCLayout() {
  const { state, slotOrder } = useDSC();
  const { dispatch: cartDispatch } = useCart();
  const { isDesktop } = useMediaQuery();
  const { hasPermission } = useAuth();
  const canOrder = hasPermission('order');
  const couplingOn = !!state.toggles.coupling;
  const bitAdapterOn = !!state.toggles.bitAdapter;

  // G4: Add to cart from DSC results
  const handleAddToCart = useCallback((product) => {
    cartDispatch({
      type: 'ADD_ITEM',
      payload: {
        productId: product.itemCode,
        name: product.description,
        sku: product.itemCode,
        itemCode: product.itemCode,
        description: product.description,
        thumbnailUrl: product.imageUrl || null,
        productLine: product.productLine,
        subcategory: product.subcategory,
        quantity: 1,
      },
    });
  }, [cartDispatch]);

  // Track active tab on the results section. Defaults to first visible tab.
  const [activeTab, setActiveTab] = useState(null);
  const initialTab = useMemo(() => {
    if (activeTab) return activeTab;
    if (!couplingOn) {
      return 'shank-adapters';
    }
    return 'shank-adapters';
  }, [activeTab, couplingOn]);

  // Results panel is ALWAYS visible (matches DTH layout; per Vandana
  // 20 May walkthrough). Removes the legacy `showDSCPLP` gate so tab
  // counts and the empty-state-with-base-products are confirmable upfront
  // without needing to make a selection first.
  const facetIds = DSC_TAB_FACETS[initialTab] || [];

  // Per-tab filtered products — narrows the full TH catalog to the products
  // that belong on the active results tab (PGMJ+PGMN tuple) AND match the
  // current cascade selections (mfr, model, thread, etc.). Recomputed when
  // tab or selections change. See services/dscTabFilter.js.
  const tabProducts = useMemo(
    () => filterProductsForTab(state.products || [], initialTab, state.selections, state.dscFilteringData),
    [state.products, initialTab, state.selections, state.dscFilteringData]
  );

  return (
    <div
      className="flex flex-col gap-6"
      data-testid="th-dsc-layout"
      data-coupling={couplingOn ? 'on' : 'off'}
      data-bit-adapter={bitAdapterOn ? 'on' : 'off'}
    >
      {/* Description banner — replaces legacy orange-border treatment. */}
      <DescriptionCard title="How to use">
        Build your top-hammer drill string from shank adapter to bit. Toggle
        Coupling or Bit adapters required if your rig configuration needs
        them.
      </DescriptionCard>

      {/* Toggle strip */}
      <div className="flex flex-col md:flex-row gap-3 md:gap-4">
        <CouplingToggle />
        <BitAdapterToggle />
      </div>

      {/* Slot card row — 3 / 4 / 5 cards depending on toggles.
          Vertical stack on mobile, horizontal on lg+. */}
      <div
        className="flex flex-col lg:flex-row lg:items-stretch gap-4"
        data-testid="th-dsc-slot-row"
      >
        {slotOrder.map((slotId) => {
          switch (slotId) {
            case 'shank-adapter':
              return <div key={slotId} className="flex-1"><THShankSlot /></div>;
            case 'couplings':
              return <div key={slotId} className="flex-1"><THCouplingsSlot /></div>;
            case 'rod':
              return <div key={slotId} className="flex-1"><THRodSlot couplingOn={couplingOn} /></div>;
            case 'bit-adapter':
              return <div key={slotId} className="flex-1"><THBitAdapterSlot /></div>;
            case 'drill-bit':
              return <div key={slotId} className="flex-1"><THDrillBitSlot /></div>;
            default:
              return null;
          }
        })}
      </div>

      {/* Results section — always visible (matches DTH; per Vandana 20 May). */}
      <div className="flex flex-col gap-3 pt-4 border-t border-n2">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="font-sandvik-headline font-normal text-lg text-carbon-black">
            Results
          </h2>
          <RestartConfiguratorButton />
        </div>
        <DSCResultsTabs
          activeTab={initialTab}
          onTabChange={setActiveTab}
        />
        <div className="flex gap-6">
          <div className="flex-1 min-w-0">
            <DSCResultsTabPanel
              tabSlug={initialTab}
              products={tabProducts}
              facetIds={facetIds}
              onAddToCart={canOrder ? handleAddToCart : undefined}
            />
          </div>
          {isDesktop && canOrder && (
            <aside className="w-72 flex-shrink-0" data-testid="minicart-column">
              <MiniCart />
            </aside>
          )}
        </div>
      </div>
    </div>
  );
}